import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Badge, Button, CopyButton, Drawer, Radio, RadioGroup, Table } from "@opendate-app/setlist";
import { CARD_STATUS_COLORS, CARD_STATUS_LABELS, LEDGER_KIND_LABELS, formatDate } from "../../GiftCards/giftCardLabels";
import { formatCents } from "../../GiftCardProducts/giftCardFormat";
import { postJson, submitPrintForm } from "../myGiftCardsRequests";
import { giftCardShape } from "../myGiftCardPropTypes";
import { GiftCardArt, expiryText } from "./GiftCardTile";

const UNAVAILABLE_MESSAGE = "Something went wrong. Please try again.";

const historyColumns = (currency) => [
  { key: "date", header: "Date", render: (entry) => formatDate(entry.createdAt) },
  {
    key: "activity",
    header: "Activity",
    render: (entry) => [LEDGER_KIND_LABELS[entry.kind] || entry.kind, entry.venueName].filter(Boolean).join(" · "),
  },
  { key: "amount", header: "Amount", align: "right", render: (entry) => formatCents(entry.amountCents, currency) },
  { key: "balance", header: "Balance", align: "right", render: (entry) => formatCents(entry.balanceAfterCents, currency) },
];

export const REVEAL_WARNING = "This replaces your code. The code in your email or on a printout will stop working.";
export const emailWarning = (email) =>
  `We'll email a new code to ${email}. The code in your earlier email or on a printout will stop working.`;

const GiftCardDrawer = ({ giftCard, giftCards, revealedCode, onCodeRevealed, onCodeEmailed, onClose, onMerged }) => {
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState(null);

  useEffect(() => {
    setError(null);
    setNotice(null);
    setConfirming(null);
    setMergeTargetId(null);
  }, [giftCard && giftCard.id]);

  if (!giftCard) return null;

  const mergeTargets = giftCards.filter((card) => giftCard.mergeTargetIds.includes(card.id));
  const mergeTarget = mergeTargets.find((card) => card.id === mergeTargetId);
  const active = giftCard.status === "active";

  const runCodeAction = async (path, onSuccess) => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const { ok, data } = await postJson(path);
      if (!ok) {
        setError((data.errors && data.errors[0]) || UNAVAILABLE_MESSAGE);
        return;
      }
      onSuccess(data);
    } catch (_requestError) {
      setError(UNAVAILABLE_MESSAGE);
    } finally {
      setConfirming(null);
      setBusy(false);
    }
  };

  const revealCode = () => runCodeAction(giftCard.codeRevealPath, (data) => onCodeRevealed(giftCard.id, data.code));

  const emailCode = () =>
    runCodeAction(giftCard.codeEmailPath, (data) => {
      onCodeEmailed(giftCard.id);
      setNotice(`We're emailing a new code to ${data.email}.`);
    });

  const print = () => submitPrintForm(giftCard.printablePath, revealedCode);

  const merge = async () => {
    setError(null);
    setBusy(true);
    try {
      const { ok, data } = await postJson(giftCard.mergePath, { target_gift_card_id: mergeTargetId });
      if (!ok) {
        setError((data.errors && data.errors[0]) || UNAVAILABLE_MESSAGE);
        return;
      }
      onMerged(data, mergeTarget);
    } catch (_requestError) {
      setError(UNAVAILABLE_MESSAGE);
    } finally {
      setBusy(false);
    }
  };

  const confirmation = {
    reveal: { description: REVEAL_WARNING, label: "Show new code", onConfirm: revealCode },
    email: { description: emailWarning(giftCard.ownerEmail), label: "Email new code", onConfirm: emailCode },
  }[confirming];

  return (
    <Drawer
      open
      onClose={onClose}
      size="md"
      title={giftCard.brandName}
      subtitle={giftCard.whereItWorks}
    >
      <div className="my-gift-card-drawer">
        {error && <Alert color="error" role="alert" title={error} />}
        {notice && <Alert color="success" title={notice} onDismiss={() => setNotice(null)} />}

        <GiftCardArt giftCard={giftCard} />

        <div className="my-gift-card-drawer-summary">
          <div>
            <div className="my-gift-card-drawer-balance">{formatCents(giftCard.balanceCents, giftCard.currency)}</div>
            <div className="my-gift-card-drawer-muted">
              {`of ${formatCents(giftCard.initialValueCents, giftCard.currency)} · ${expiryText(giftCard)}`}
            </div>
          </div>
          <Badge color={CARD_STATUS_COLORS[giftCard.status]} size="md">
            {CARD_STATUS_LABELS[giftCard.status] || giftCard.status}
          </Badge>
        </div>

        {active && (
          <section className="my-gift-card-drawer-section" aria-label="Gift card code">
            <h3 className="my-gift-card-drawer-heading">Gift card code</h3>
            <div className="my-gift-card-drawer-code">{revealedCode || `••••-••••-••••-${giftCard.last4}`}</div>
            {confirmation ? (
              <Alert
                color="warning"
                title="Get a new code?"
                description={confirmation.description}
                actions={
                  <div className="my-gift-card-drawer-actions">
                    <Button variant="primary" size="sm" disabled={busy} onClick={confirmation.onConfirm}>
                      {confirmation.label}
                    </Button>
                    <Button variant="tertiary" size="sm" disabled={busy} onClick={() => setConfirming(null)}>
                      Cancel
                    </Button>
                  </div>
                }
              />
            ) : (
              <div className="my-gift-card-drawer-actions">
                {revealedCode ? (
                  <CopyButton value={revealedCode} label="Copy code" copiedLabel="Copied" size="sm" />
                ) : (
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => setConfirming("reveal")}>
                    Show code
                  </Button>
                )}
                <Button variant="secondary" size="sm" disabled={busy} onClick={() => setConfirming("email")}>
                  Email me my code
                </Button>
                <Button variant="secondary" size="sm" disabled={busy || !revealedCode} onClick={print}>
                  Print
                </Button>
              </div>
            )}
            {!revealedCode && <p className="my-gift-card-drawer-muted">Show the code to print it.</p>}
          </section>
        )}

        {active && (
          <section className="my-gift-card-drawer-section" aria-label="Merge">
            <h3 className="my-gift-card-drawer-heading">Merge into another card</h3>
            {mergeTargets.length === 0 ? (
              <p className="my-gift-card-drawer-muted">
                You can merge cards from the same venue that work at the same places and use the same currency. You
                don&apos;t have another card like that.
              </p>
            ) : (
              <>
                <RadioGroup
                  name={`merge-target-${giftCard.id}`}
                  value={mergeTargetId}
                  onChange={(value) => setMergeTargetId(Number(value))}
                >
                  {mergeTargets.map((card) => (
                    <Radio
                      key={card.id}
                      id={`merge-target-${giftCard.id}-${card.id}`}
                      value={card.id}
                      label={`•••• ${card.last4} · ${formatCents(card.balanceCents, card.currency)}`}
                    />
                  ))}
                </RadioGroup>
                {mergeTarget && (
                  <Alert
                    color="warning"
                    title={`Move ${formatCents(giftCard.balanceCents, giftCard.currency)} onto •••• ${mergeTarget.last4}?`}
                    description="This card will be emptied and can't be undone. The merged card keeps the later expiry date."
                    actions={
                      <Button variant="primary" size="sm" disabled={busy} onClick={merge}>
                        Merge cards
                      </Button>
                    }
                  />
                )}
              </>
            )}
          </section>
        )}

        <section className="my-gift-card-drawer-section" aria-label="History">
          <h3 className="my-gift-card-drawer-heading">History</h3>
          <Table
            columns={historyColumns(giftCard.currency)}
            data={giftCard.history}
            getRowKey={(entry) => entry.id}
          />
        </section>
      </div>
    </Drawer>
  );
};

GiftCardDrawer.propTypes = {
  giftCard: giftCardShape,
  giftCards: PropTypes.arrayOf(giftCardShape).isRequired,
  revealedCode: PropTypes.string,
  onCodeRevealed: PropTypes.func.isRequired,
  onCodeEmailed: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onMerged: PropTypes.func.isRequired,
};

export default GiftCardDrawer;
